import React from "react";
import { useTranslation } from "react-i18next";
import { Pencil, Trash2 } from "lucide-react";
import { Absence } from "../../features/absences/models/Absence";

interface AbsenceListProps {
  absences: Absence[];
  memberNameById: Map<string, string>;
  onEdit: (absence: Absence) => void;
  onDelete: (absence: Absence) => void;
}

const formatRange = (locale: string, startDate: string, endDate: string) => {
  const format = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" });
  const start = format.format(new Date(`${startDate}T00:00:00`));
  const end = format.format(new Date(`${endDate}T00:00:00`));
  return startDate === endDate ? start : `${start} – ${end}`;
};

/** The absences of the visible range as rows with edit/delete actions. */
const AbsenceList: React.FC<AbsenceListProps> = ({
  absences,
  memberNameById,
  onEdit,
  onDelete,
}) => {
  const { t, i18n } = useTranslation();

  if (absences.length === 0) {
    return (
      <p className="text-sm text-grey-40 dark:text-grey-80">
        {t("absences.empty", "No absences in this range.")}
      </p>
    );
  }

  return (
    <ul className="space-y-2">
      {absences.map((absence) => (
        <li
          key={absence.id}
          className="with-border flex items-center gap-3 rounded-2xl bg-white p-3 dark:bg-primary-10"
        >
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-grey-20 dark:text-white">
              {memberNameById.get(absence.memberId) ??
                t("absences.unknownMember", "Former member")}
            </p>
            <p className="text-sm text-grey-40 dark:text-grey-80">
              {formatRange(i18n.language, absence.startDate, absence.endDate)}
              {absence.note ? ` · ${absence.note}` : ""}
            </p>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onEdit(absence)}
            aria-label={t("absences.editTitle", "Edit absence")}
          >
            <Pencil size={16} />
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onDelete(absence)}
            aria-label={t("absences.deleteAction", "Delete absence")}
          >
            <Trash2 size={16} />
          </button>
        </li>
      ))}
    </ul>
  );
};

export default AbsenceList;
